import React, { useState } from "react";
import { DEFAULT_PAGE_SIZE, PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputAdornment,
  Switch,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Search,
  Visibility,
  Close,
  Download,
  Upload,
  AdminPanelSettings,
} from "@mui/icons-material";
import DataImportWizard from "../../components/common/DataImportWizard";
import PermissionGate from "../../components/common/PermissionGate";
import {
  useCreateUser,
  useUsers,
  useUpdateUser,
} from "../../hooks/queries/useUsers";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../hooks/queries/queryKeys";
import { revealUserPii } from "../../services/piiService";
import { usePermissions } from "../../hooks/usePermissions";
import {
  validateEmail,
  validatePhone,
  validateRequired,
} from "../../utils/validation";

const ROLE_SUPER_ADMIN = "superAdmin";

interface FormData {
  name: string;
  email: string;
  phone: string;
  institute: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  institute?: string;
  submit?: string;
}

interface SuperAdminRow {
  id: string | number;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  phone?: string;
  status?: string | boolean;
  institute?: string;
  fullName?: string;
  profileImage?: string;
  role?: string;
}

const SuperAdminUsers: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    institute: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isCreating, setIsCreating] = useState(false);

  const { data: usersResp, isLoading: usersLoading, refetch: refetchSuperAdmins } = useUsers(
    { role: ROLE_SUPER_ADMIN },
    { refetchOnMount: "always", staleTime: 30_000 }
  );
  const superAdmins: SuperAdminRow[] = (
    usersResp?.data?.data?.users || usersResp?.data?.data || []
  ).filter((u: SuperAdminRow) => {
    const r = String(u?.role || "").trim().toLowerCase();
    return r === "superadmin" || r === "super admin" || r === "super_admin";
  });

  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const [selectedUser, setSelectedUser] = useState<SuperAdminRow | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // PII reveal-to-edit wiring (ported from AllUsersManagement.js).
  // superAdmin holds this slug implicitly server-side; gate the UI on the same slug.
  const { hasPermission } = usePermissions();
  const canRevealUserPii = hasPermission("user.pii.reveal");
  // A value is masked when the backend returns a "***"-style marker.
  const looksMasked = (v?: string): boolean =>
    typeof v === "string" && v.includes("***");
  // Whether a reveal request is in flight (used to disable the reveal button).
  const [revealingUserId, setRevealingUserId] = useState<number | null>(null);

  const showToast = (
    message: string,
    severity: "success" | "error" | "info" | "warning" = "success"
  ) => {
    setToastMessage(message);
    setToastSeverity(severity);
  };
  const handleCloseToast = () => setToastMessage(null);

  // Reveal a super admin's real email/phone and merge into the open edit form.
  // Never persisted; only updates local component state. No-crash: wrapped in
  // try/catch, missing fields fall back to previous form values.
  const handleRevealUserPii = async (userId: number) => {
    if (!userId || revealingUserId) return;
    setRevealingUserId(userId);
    try {
      const res = await revealUserPii(userId);
      const real: { email?: string; phoneNumber?: string; phone?: string } =
        res?.data?.data || {};
      const realPhone = real.phoneNumber ?? real.phone;
      setFormData((prev) => ({
        ...prev,
        email: real.email !== undefined && real.email !== null ? real.email : prev.email,
        phone:
          realPhone !== undefined && realPhone !== null ? realPhone : prev.phone,
      }));
      // Clear any masked-value errors now that real values are present.
      setFormErrors((prev) => ({ ...prev, email: undefined, phone: undefined }));
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      showToast(
        e?.response?.data?.message || "Could not reveal personal information",
        "error"
      );
    } finally {
      setRevealingUserId(null);
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    const nameError = validateRequired(formData.name, "Full name");

    if (nameError) errors.name = nameError;

    // In EDIT mode a still-masked email / empty (masked rendered) phone means
    // "unchanged" — it is omitted from the payload so the backend keeps the real
    // value. Do not hard-fail validation on those; only validate what the user
    // actually typed. CREATE always validates fully.
    const emailUnchanged = !!editingId && looksMasked(formData.email);
    const phoneDigits = String(formData.phone || "").replaceAll(/\D/g, "");
    const phoneUnchanged =
      !!editingId && (!phoneDigits || looksMasked(formData.phone));

    if (!emailUnchanged) {
      const emailError = validateEmail(formData.email);
      if (emailError) errors.email = emailError;
    }
    if (!phoneUnchanged) {
      const phoneError = validatePhone(formData.phone);
      if (phoneError) errors.phone = phoneError;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;
    try {
      setIsCreating(true);
      const nameParts = (formData.name || "").trim().split(/\s+/).filter(Boolean);
      const firstName = nameParts[0] || "";
      const lastName =
        nameParts.length > 1 ? nameParts.slice(1).join(" ") : nameParts[0] || "";
      if (editingId) {
        // Never overwrite real PII with a mask: OMIT a still-masked email and an
        // empty/masked phone so the backend keeps the stored real value.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const userData: any = { firstName, lastName };
        if (!looksMasked(formData.email)) userData.email = formData.email;
        const phoneDigits = String(formData.phone || "").replaceAll(/\D/g, "");
        if (phoneDigits && !looksMasked(formData.phone))
          userData.phoneNumber = formData.phone;
        await updateMutation.mutateAsync({
          id: editingId,
          userData,
        });
        showToast("Super admin updated successfully.", "success");
      } else {
        await createMutation.mutateAsync({
          firstName,
          lastName,
          email: formData.email,
          phoneNumber: formData.phone,
          role: ROLE_SUPER_ADMIN,
        });
        showToast("Super admin created successfully.", "success");
      }
      closeCreateDialog();
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    } catch (err) {
      const error = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errMsg =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong.";
      setFormErrors({ submit: errMsg });
      showToast(errMsg, "error");
    } finally {
      setIsCreating(false);
    }
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setFormData({ name: "", email: "", phone: "", institute: "" });
    setFormErrors({});
    setEditingId(null);
    setIsCreating(false);
  };

  const blockNumberKeyDown = (e: React.KeyboardEvent) => {
    if (/^\d$/.test(e.key)) e.preventDefault();
  };
  const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replaceAll(/\d/g, "");
    setFormData((prev) => ({ ...prev, name: value }));
    if (formErrors.name)
      setFormErrors((prev) => ({ ...prev, name: undefined }));
  };

  const validateEmailLive = (email: string) => validateEmail(email) || "";
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, email: value }));
    const emailError = validateEmailLive(value);
    setFormErrors((prev) => ({ ...prev, email: emailError }));
  };

  const validatePhoneLive = (phone: string) => validatePhone(phone) || "";
  const handlePhoneChange = (phone: string) => {
    setFormData((prev) => ({ ...prev, phone }));
    const phoneError = validatePhoneLive(phone);
    setFormErrors((prev) => ({ ...prev, phone: phoneError }));
  };

  const isFormValid = React.useMemo(() => {
    const nameOk = !validateRequired(formData.name, "Full name");
    // Mirror validateForm: in edit mode a masked email / empty-or-masked phone
    // is "unchanged" (valid), not an error.
    const emailUnchanged = !!editingId && looksMasked(formData.email);
    const phoneDigits = String(formData.phone || "").replaceAll(/\D/g, "");
    const phoneUnchanged =
      !!editingId && (!phoneDigits || looksMasked(formData.phone));
    const emailOk = emailUnchanged || !validateEmail(formData.email);
    const phoneOk = phoneUnchanged || !validatePhone(formData.phone);
    const hasErrors = Object.entries(formErrors)
      .filter(([key]) => key !== "submit")
      .some(([, value]) => Boolean(value));
    return nameOk && emailOk && phoneOk && !hasErrors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.name, formData.email, formData.phone, formErrors, editingId]);

  const normalizedRows = React.useMemo(() => {
    return (superAdmins || []).map((row) => ({
      ...row,
      id: row.id ?? (row as { _id?: string })._id ?? String(row.email ?? Math.random()),
      name:
        row.name ||
        `${row.firstName || ""} ${row.lastName || ""}`.trim() ||
        row.email ||
        "",
      phoneNumber: row.phoneNumber ?? row.phone ?? "",
      status:
        row.status === true || row.status === "active" ? "active" : "inactive",
    }));
  }, [superAdmins]);

  const filteredSuperAdmins = React.useMemo(() => {
    if (!searchQuery?.trim()) return normalizedRows;
    const q = searchQuery.trim().toLowerCase();
    return normalizedRows.filter((row) => {
      const name = (row.name || "").toLowerCase();
      const email = (row.email || "").toLowerCase();
      const phone = String(row.phoneNumber || "").replaceAll(/\D/g, "");
      const queryDigits = q.replaceAll(/\D/g, "");
      return (
        name.includes(q) ||
        email.includes(q) ||
        (queryDigits.length > 0 && phone.includes(queryDigits))
      );
    });
  }, [normalizedRows, searchQuery]);

  const columns = [
    {
      field: "name",
      headerName: "Admin User",
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "error.main", width: 40, height: 40 }}>
            <AdminPanelSettings />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "phoneNumber",
      headerName: "Phone",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.row?.phoneNumber || params.row?.phone || "—"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const rowId = Number(params.row.id);
        const isActive = params.row.status === "active";
        const isUpdating = statusUpdatingId === rowId;
        return (
          <Box sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>
            {isUpdating ? (
              <CircularProgress size={22} aria-label="Updating status" />
            ) : (
              <Switch
                checked={isActive}
                size="small"
                disabled={isUpdating}
                onChange={async (e) => {
                  e.stopPropagation();
                  const newStatusBool = !isActive;
                  setStatusUpdatingId(rowId);
                  try {
                    const userService = (
                      await import("../../services/userService")
                    ).default;
                    await userService.updateAdminStatus(rowId, newStatusBool);
                    await queryClient.invalidateQueries({
                      queryKey: queryKeys.users.lists(),
                    });
                    showToast(
                      newStatusBool
                        ? "User activated successfully."
                        : "User deactivated successfully.",
                      "success"
                    );
                  } catch (err) {
                    const error = err as {
                      response?: { data?: { message?: string } };
                      message?: string;
                    };
                    const msg =
                      error?.response?.data?.message ||
                      error?.message ||
                      "Failed to update status. Please try again.";
                    showToast(msg, "error");
                  } finally {
                    setStatusUpdatingId(null);
                  }
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedUser(params.row as SuperAdminRow);
              setViewDialogOpen(true);
            }}
          >
            <Visibility />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditingId(params.row.id as number);
              setFormData({
                name:
                  params.row.name ||
                  `${params.row.firstName || ""} ${params.row.lastName || ""}`,
                email: params.row.email || "",
                phone: params.row.phoneNumber || params.row.phone || "",
                institute: params.row.institute || "",
              });
              setCreateDialogOpen(true);
            }}
          >
            <Edit />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Super Admin Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage platform administrators
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGate permission="user.import">
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
          </PermissionGate>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={async () => {
              try {
                const userService = (
                  await import("../../services/userService")
                ).default;
                const blob = await userService.exportUsers({
                  role: ROLE_SUPER_ADMIN,
                  page: 1,
                  limit: 10000,
                });
                const url = globalThis.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                  "download",
                  `superadmins_export_${Date.now()}.csv`
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                globalThis.URL.revokeObjectURL(url);
              } catch (err) {
                console.error("Export failed:", err);
              }
            }}
            disabled={superAdmins.length === 0}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingId(null);
              setFormData({ name: "", email: "", phone: "", institute: "" });
              setFormErrors({});
              setCreateDialogOpen(true);
            }}
          >
            Add Super Admin
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: "Total Super Admins", value: superAdmins.length },
          {
            label: "Active",
            value: superAdmins.filter((a) => a.status === "active").length,
          },
          {
            label: "Platform Admins",
            value: superAdmins.filter(
              (a) => a.institute === "All Institutes"
            ).length,
          },
          {
            label: "Institute Admins",
            value: superAdmins.filter(
              (a) => a.institute !== "All Institutes"
            ).length,
          },
        ].map((stat) => (
          <Grid item xs={6} sm={3} key={stat.label}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              placeholder="Search admins..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <DataGrid
            rows={filteredSuperAdmins}
            columns={columns}
            getRowId={(row) => row.id}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } } }}
            sx={{ border: "none" }}
            loading={!usersResp}
          />
        </CardContent>
      </Card>

      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewDialogOpen(false);
          setSelectedUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>View Super Admin</DialogTitle>
        <DialogContent>
          {selectedUser ? (
            <Box sx={{ py: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar
                    sx={{ width: 64, height: 64 }}
                    src={selectedUser.profileImage || ""}
                  >
                    {String(
                      selectedUser.firstName || selectedUser.fullName || ""
                    ).charAt(0)}
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedUser.fullName || selectedUser.firstName}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                    <Chip label={selectedUser.role} size="small" />
                    <Chip
                      label={
                        selectedUser.status === true ||
                        selectedUser.status === "active"
                          ? "Active"
                          : "Inactive"
                      }
                      size="small"
                      color={
                        selectedUser.status === true ||
                        selectedUser.status === "active"
                          ? "success"
                          : "default"
                      }
                    />
                  </Box>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>
                    {selectedUser.email || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography>
                    {selectedUser.phoneNumber || selectedUser.phone || "-"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              setSelectedUser(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          closeCreateDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {editingId ? "Edit Super Admin" : "Add Super Admin"}
            <IconButton onClick={closeCreateDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Full Name"
                value={formData.name}
                onChange={handleFullNameChange}
                onKeyDown={blockNumberKeyDown}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                required
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleEmailChange}
                onBlur={(e) => {
                  const emailError = validateEmailLive(e.target.value);
                  if (emailError)
                    setFormErrors((prev) => ({ ...prev, email: emailError }));
                }}
                error={!!formErrors.email}
                helperText={formErrors.email}
                placeholder="e.g. admin@example.com"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth error={!!formErrors.phone} required>
                <PhoneInput
                  country={"in"}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  specialLabel="Phone *"
                  inputStyle={{ width: "100%", height: 56, borderRadius: 8 }}
                  containerStyle={{ width: "100%" }}
                />
                {formErrors.phone && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {formErrors.phone}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            {editingId &&
              canRevealUserPii &&
              (looksMasked(formData.email) ||
                looksMasked(formData.phone) ||
                !String(formData.phone || "").replaceAll(/\D/g, "")) && (
                <Grid item xs={12}>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    disabled={revealingUserId === editingId}
                    onClick={() => handleRevealUserPii(editingId)}
                  >
                    {revealingUserId === editingId
                      ? "Revealing..."
                      : "Reveal email & phone"}
                  </Button>
                </Grid>
              )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={isCreating || !isFormValid}
          >
            {(() => {
              if (isCreating) return editingId ? "Updating..." : "Creating...";
              return editingId ? "Update Admin" : "Create Admin";
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      <DataImportWizard
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityType="superadmins"
        onImportComplete={() =>
          queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() })
        }
        instituteId={undefined}
      />

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toastSeverity}
          onClose={handleCloseToast}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuperAdminUsers;
